import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators } from "redux";
import { bindAction } from "interfaces";
import { Routes, Route, useParams, useLocation, useNavigate } from "react-router";
import { intersection } from "lodash";

import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { type PacksListingProps } from "./types";
import * as packListingActions from "../state/thunks/packListingThunk";
import * as packEntityStateActions from "../state/actions/packEntityStateActions";
import Segments from "components/navigation/segments/Segments";
import RemovePackFromAccountConfirmationModal from "components/packs/removePackConfirmationDialog/RemovePackConfirmationDialog";
import { type EnablePackTrialRequest, type MediaItem, type PackRequest, type EnablePackRequest } from "../types/state";
import { PackListingsOverview } from "./Overview/PackListingsOverview";
import { RouteNames } from "../../../../enums";
import { ListingOverviewHeader } from "./ListingOverviewHeader/ListingOverviewHeader";
import { fetchOwnAccountInfo as fetchOwnAccountInfoAction } from "../state/thunks/ownAccountInfoThunk";
import { ownAccountNameSelector } from "../state/slices/ownAccountInfoSlice";
import * as backgroundTaskOperations from "../../../BackgroundTasks/state/backgroundTasksActions";
import * as notificationOperations from "../../../Notifications/state/notificationsActions";
import backgroundTask from "../../../BackgroundTasks/backgroundTask";
import { DeleteLinkButton } from "../../../../components/buttons/linkButtons";
import packsDataService from "../services/packsDataService";
import { MediaItemsCarousel } from "../../../../components/packs/mediaItemsCarousel/MediaItemsCarousel";
import MediaItemType from "../../../../enums/licensing/mediaItemType";
import DeletePackConfirmationDialog from "../../../../components/packs/deletePackConfirmationDialog/DeletePackConfirmationDialog";
import { RequestPackModals } from "../PackRequestModals/RequestPackModals";
import "features/Licensing/Packs/Listings/PackListingsSegments.scss";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { fetchAddOns } from "features/Accounts/state/thunks/addOnsThunk";
import { isPurchasePackAddonEnabled } from "../utils/packUtils";
import { removePackFromAccount } from "features/Accounts/state/thunks/accountPacksThunk";
import { addContentToPackPermissions } from "enums/rights";

const pages = {
  overview: {
    index: 0,
    segment: {
      label: "Overview",
    },
  },
} as const;

export type Props = PacksListingProps & PropsFromRedux;

export const PackListingsSegments: React.FC<Props> = ({
  pack,
  accountName,
  packActions,
  accountPacksActions,
  packEntityActions,
  fetchOwnAccountInfo,
  packRequestModal,
  isLoading,
  accountId,
  currentUserId,
  backgroundTasksActions,
  notificationsActions,
  userPermissions,
  fetchAddOns,
  addOns,
  accountTypeId,
  parentAccountTypeId,
}) => {
  const params = useParams();
  const { id, title, isPurchased, isExpired = false, isOwn, isRequested, isTrial, expirationDate, isFree } = pack;
  const packId = Number(params.id);
  const location = useLocation();
  const navigate = useNavigate();
  const isPaidPacksEnabled = !!useFeatureFlag(FeatureFlags.PaidPacks);
  const isTrialAllowed = isPaidPacksEnabled ? pack.pricingSettings?.isTrialAllowed ?? false : pack.isTrialAllowed;
  const trialDuration = isPaidPacksEnabled ? pack.pricingSettings?.trialPeriod?.duration ?? 0 : pack.trialDuration;

  const { publisher, logoUri } = useMemo(() => {
    return {
      publisher: location.state?.publisher,
      logoUri: location.state?.logoUri,
    };
  }, [location.state]);
  const [isTrialRequest, setIsTrialRequest] = useState(false);

  const [packUsageCount, setPackUsageCount] = React.useState(0);
  const [triggerOpenDeletePackDialog, setTriggerOpenDeletePackDialog] = React.useState(false);
  const [triggerOpenRemovePackDialog, setTriggerOpenRemovePackDialog] = React.useState(false);

  const hasPacksManageRole = intersection(userPermissions, addContentToPackPermissions).length > 0;

  const handleFreePackAddedSuccess = useCallback(() => {
    if (isFree) {
      navigate(`/${RouteNames.purchasedPacks}/${id}/licensing`);
    }
  }, [isFree, id, navigate]);

  const handleTrialPackAddedSuccess = useCallback(
    () => navigate(`/${RouteNames.purchasedPacks}/${id}/licensing`),
    [id, navigate],
  );
  const handleRemovePackSuccess = useCallback(() => packActions.getPackListing(packId), [packId, packActions]);

  const requestPack = useCallback(
    (packRequest: PackRequest) => {
      packActions.hidePackRequestModal();
      packActions.requestPack(packRequest);
    },
    [packActions],
  );

  const enablePackTrial = useCallback(
    (req: EnablePackTrialRequest) => {
      packActions.hidePackRequestModal();
      packActions.enablePackTrial(req, handleTrialPackAddedSuccess);
    },
    [packActions, handleTrialPackAddedSuccess],
  );

  const enablePack = useCallback(
    (req: EnablePackRequest) => {
      packActions.hidePackRequestModal();
      packActions.enablePack(req, isPaidPacksEnabled, handleFreePackAddedSuccess);
    },
    [packActions, isPaidPacksEnabled, handleFreePackAddedSuccess],
  );

  const handleEditClick = () => {
    const goToPackEdit = (payload: { id: number }) => navigate(`/${RouteNames.licensingPacks}/${payload.id}`);

    if (!pack.isDraft) {
      packEntityActions.fetchDraftPackEntity(id);
    }
    goToPackEdit({ id });
  };

  const handleClose = () => {
    navigate(`/${RouteNames.availablePacks}`);
  };

  const mediaItems: MediaItem[] = [{ url: pack.thumbnailImageUrl, typeId: MediaItemType.Image }, ...pack.mediaItems];

  useEffect(() => {
    packActions.getPackListing(packId);
    fetchOwnAccountInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAddOns(accountId);
  }, [accountId, fetchAddOns]);

  const hasPurchasePackAddon = useMemo(() => isPurchasePackAddonEnabled(addOns), [addOns]);

  const onCancelPackDelete = () => {
    setTriggerOpenDeletePackDialog(false);
  };

  const onContinuePackDelete = async () => {
    setTriggerOpenDeletePackDialog(false);

    const params = {
      id: "DeletePack",
      title: `Deletion of pack`,
      getMessageIds: async () => {
        const { data } = await packsDataService.deletePack(packId);
        return [data];
      },
      onCompleted: handleClose,
      successTransientMessage: `Pack was deleted!`,
      failureTransientMessage: `Pack delete failed!`,
    };

    const { addOperationV1 } = backgroundTasksActions;
    const { sendTransientNotification } = notificationsActions;

    await backgroundTask.updateEntity(params, {
      addOperation: addOperationV1,
      sendTransientNotification,
    });
  };

  const handleDeleteClick = async () => {
    let usageCount = await packsDataService.getPackUsageCount(packId);
    setPackUsageCount(usageCount);
    setTriggerOpenDeletePackDialog(true);
  };

  const renderActionButtons = () => [
    <DeleteLinkButton key={1} onClick={handleDeleteClick} isDisabled={!hasPacksManageRole} />,
  ];

  const handlePackRequest = () => {
    setIsTrialRequest(false);
    packActions.showPackRequestModal();
  };

  const handlePackTrialRequest = () => {
    setIsTrialRequest(true);
    packActions.showPackRequestModal();
  };

  const handlePackPurchaseSuccess = useCallback(
    (packId: number) => {
      packActions.hidePackRequestModal();
      navigate(`/${RouteNames.purchasedPacks}/${packId}/licensing`);
    },
    [packActions, navigate],
  );

  const onContinuePackRemove = () => {
    setTriggerOpenRemovePackDialog(false);
    accountPacksActions.removePackFromAccount(accountId, packId, handleRemovePackSuccess);
  };

  const handleRemovePack = () => {
    setTriggerOpenRemovePackDialog(true);
  };

  const onCancelPackRemove = () => {
    setTriggerOpenRemovePackDialog(false);
  };

  return (
    <div className="pack-overview-container">
      <div className="nested-content pack-listing-overview pack-listing-overview-header">
        <ListingOverviewHeader
          {...pack}
          titleForGA="Pack Details"
          backButton
          defaultURL={`/${RouteNames.availablePacks}`}
          onPackRequest={handlePackRequest}
          onPackRemove={handleRemovePack}
          onPackTrialRequest={handlePackTrialRequest}
          onEditClick={handleEditClick}
          renderActionButtons={renderActionButtons}
          isExpired={isExpired}
          isArchived={false}
          isTrial={isTrial}
          expirationDate={expirationDate}
          isPurchased={isPurchased}
          isOwn={isOwn}
          isRequested={isRequested}
          isTrialAllowed={isTrialAllowed}
          publisher={publisher}
          logoUri={logoUri}
          trialPeriodName={pack.pricingSettings?.trialPeriod?.name}
          hasPurchasePackAddon={hasPurchasePackAddon}
        />
      </div>

      <div className="scrollable-content">
        <MediaItemsCarousel items={mediaItems} />
        <div className="nested-content pack-listing-overview pack-overview-content">
          <Segments to={`/licensing/packs/listings/${packId}`}>
            {[<Segments.Segment key={pages.overview.index} {...pages.overview.segment} />]}
          </Segments>
          <Routes>
            <Route
              path="/"
              element={
                <PackListingsOverview
                  pack={pack}
                  hasPurchasePackAddon={hasPurchasePackAddon}
                  accountId={accountId}
                  isLoading={isLoading}
                  onPackRequest={handlePackRequest}
                  onPackTrialRequest={handlePackTrialRequest}
                  onPackRemove={handleRemovePack}
                  parentAccountTypeId={parentAccountTypeId}
                  accountTypeId={accountTypeId}
                />
              }
            />
          </Routes>
        </div>

        <RequestPackModals
          accountId={accountId}
          packId={packId}
          packTitle={title}
          accountName={accountName}
          userId={currentUserId}
          onCancelPackRequest={packActions.hidePackRequestModal}
          onSubmitEnablePack={enablePack}
          isTrialRequest={isTrialRequest}
          showModal={packRequestModal.show}
          onSubmitPackRequest={requestPack}
          onTrialRequest={enablePackTrial}
          onTrialEnable={enablePackTrial}
          trialDuration={trialDuration}
          onPaymentSuccess={handlePackPurchaseSuccess}
          rawPricePerUser={pack.pricingSettings?.pricePerUser}
          unlimitedLicenses={pack.licensingSettings?.unlimitedLicenses}
          isPurchased={pack.isPurchased}
          isTrial={pack.isTrial}
          packPricingTypeId={pack.packPricingTypeId}
          isExpired={pack.isExpired}
          key={packId}
        />

        <DeletePackConfirmationDialog
          packUsageCount={packUsageCount}
          triggerOpen={triggerOpenDeletePackDialog}
          onCancel={onCancelPackDelete}
          onContinue={onContinuePackDelete}
        />

        <RemovePackFromAccountConfirmationModal
          triggerOpen={triggerOpenRemovePackDialog}
          onCancel={onCancelPackRemove}
          onContinue={onContinuePackRemove}
        />
      </div>
    </div>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  pack: state.packs.packListingReducer.details.pack,
  isLoading: state.packs.packListingReducer.details.isLoading,
  packRequestModal: state.packs.packListingReducer.details.packRequestModal,
  accountId: state.userProfile.accountId,
  accountName: ownAccountNameSelector(state),
  currentUserId: state.userProfile.id,
  userPermissions: state.userProfile.permissions,
  accountTypeId: state.userProfile.accountTypeId,
  parentAccountTypeId: state.userProfile.parentAccountTypeId,
  addOns: state.accounts.addOns,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  packActions: bindActionCreators(packListingActions, dispatch),
  packEntityActions: bindActionCreators(packEntityStateActions, dispatch),
  fetchOwnAccountInfo: () => dispatch(fetchOwnAccountInfoAction()),
  backgroundTasksActions: bindActionCreators(backgroundTaskOperations, dispatch),
  notificationsActions: bindActionCreators(notificationOperations, dispatch),
  fetchAddOns: (accountId: number) => dispatch(fetchAddOns(accountId)),
  accountPacksActions: {
    removePackFromAccount: bindAction(removePackFromAccount, dispatch),
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PackListingsSegments);
export default ConnectedComponent;
